import { useLanguage } from "../LanguageContext";
import { useTheme } from "../ThemeContext";
import { 
  ArrowRight, 
  Calendar, 
  BookOpen, 
  Coins, 
  Landmark,
  Map,
  Lightbulb,
  Quote,
  ExternalLink,
  ChevronLeft,
  Sparkles
} from "lucide-react";
import { Link } from "react-router-dom";

// Historical images (royalty-free)
const HISTORY_IMAGES = {
  coins: "https://images.unsplash.com/photo-1578575784348-fb24551be1b2?auto=format&fit=crop&w=800&q=80",
  oldQuebec: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80",
  antiqueMap: "https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&w=1200&q=80",
  coinPile: "https://images.unsplash.com/photo-1643393670577-b214e610c8f8?auto=format&fit=crop&w=800&q=80",
  worldMap: "https://images.pexels.com/photos/30247330/pexels-photo-30247330.jpeg?auto=compress&cs=tinysrgb&w=800",
  silverCoins: "https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&w=800&q=80",
  historicBuilding: "https://images.unsplash.com/photo-1569949381669-ecf31ae8e613?auto=format&fit=crop&w=800&q=80"
};

// Wikimedia Commons images (public domain) - Historical Quebec/Canada
const WIKIMEDIA_IMAGES = {
  // Louis-Joseph Papineau - Daguerréotype circa 1852, Library and Archives Canada
  papineau: "https://upload.wikimedia.org/wikipedia/commons/5/5b/Louis-Joseph_Papineau.jpg",
  
  // Billet de 5 piastres - Banque du Peuple (1836) 
  billetPiastres5: "https://upload.wikimedia.org/wikipedia/commons/3/32/Billet_de_5_piastres_de_la_Banque_du_Peuple.jpg",
  
  // Billet de 2 piastres - Banque Molson (1853)
  billetMolson: "https://upload.wikimedia.org/wikipedia/commons/c/cc/Deux_piastres_de_la_Banque_Molson_-_1853.jpg",
  
  // Billet de 1 dollar - Banque du Peuple (1836)
  billetDollar1836: "https://upload.wikimedia.org/wikipedia/commons/7/76/Banque_du_Peuple_-_billet_de_1_dollar_-_1836.jpg",
  
  // Billet de 5 piastres - Banque Provinciale (1919)
  billetProvinciale: "https://upload.wikimedia.org/wikipedia/commons/8/8c/La_Banque_Provinciale_du_Canada_-_Cinq_piastres_-_1919.jpg",
  
  // Billet de 1 dollar - Banque de Montréal (1821)
  billetMontreal1821: "https://upload.wikimedia.org/wikipedia/commons/c/cb/Billet_de_1_dollar%2C_Banque_de_Montreal_-_1821.jpg",
  
  // Monnaie de carte - Card Money New France (using Unsplash alternative for display)
  cardMoney: "https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&w=800&q=80",
  
  // Pièce de huit espagnole - Spanish Dollar (using working silver coins image)
  pieceOfEight: "https://images.pexels.com/photos/15954089/pexels-photo-15954089.jpeg?auto=compress&cs=tinysrgb&w=800",
  
  // Carte Nouvelle-France (using antique map alternative)
  newFranceMap: "https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&w=800&q=80",
  
  // Rébellion des Patriotes 1837 
  rebellion1837: "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c9/Patriotes_%C3%A0_Beauharnois_en_novembre_1838.png/600px-Patriotes_%C3%A0_Beauharnois_en_novembre_1838.png"
};

const HistoryPage = () => {
  const { t, language } = useLanguage();
  const { palette } = useTheme();
  const h = t.history;

  const sectionIcons = {
    origins: <Coins className="w-6 h-6" />,
    newFrance: <Map className="w-6 h-6" />,
    cardMoney: <BookOpen className="w-6 h-6" />,
    transition: <Landmark className="w-6 h-6" />,
    modern: <Sparkles className="w-6 h-6" />
  };

  const sectionImages = {
    origins: WIKIMEDIA_IMAGES.pieceOfEight,
    newFrance: WIKIMEDIA_IMAGES.newFranceMap,
    cardMoney: WIKIMEDIA_IMAGES.cardMoney,
    transition: WIKIMEDIA_IMAGES.papineau,
    modern: WIKIMEDIA_IMAGES.billetPiastres5
  };

  const sectionImageCredits = {
    origins: { text: "Pièce de 8 réaux espagnole (1739) — Wikimedia Commons", show: true },
    newFrance: { text: "Carte de la Nouvelle-France — Wikimedia Commons", show: true },
    cardMoney: { text: "Monnaie de carte, Nouvelle-France — Wikimedia Commons", show: true },
    transition: { text: "Louis-Joseph Papineau, daguerréotype ~1852 — Bibliothèque et Archives Canada", show: true },
    modern: { text: "Billet de 5 piastres, Banque du Peuple (1836) — Wikimedia Commons", show: true }
  };

  const sectionColors = {
    origins: "bg-amber-50 border-amber-200",
    newFrance: "bg-emerald-50 border-emerald-200",
    cardMoney: "bg-blue-50 border-blue-200",
    transition: "bg-rose-50 border-rose-200",
    modern: "bg-violet-50 border-violet-200"
  };

  const sectionKeys = ["origins", "newFrance", "cardMoney", "transition", "modern"];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-16 pb-20 overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url(${HISTORY_IMAGES.antiqueMap})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${palette.lightPageBg}f0, ${palette.lightPageBg}cc, ${palette.lightPageBg})` }} />
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          {/* Back Button */}
          <Link 
            to="/"
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Piastre.one</span>
          </Link>

          <h1 
            data-testid="history-title"
            className="font-outfit text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-slate-900 mb-6"
          >
            {h.title}
          </h1>
          
          <p className="text-xl sm:text-2xl font-medium mb-8" style={{ color: palette.sectionAccent }}>
            {h.subtitle}
          </p>
          
          <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
            {h.intro}
          </p>
        </div>
      </section>

      {/* Timeline Overview */}
      <section className="py-16 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-outfit text-2xl font-semibold text-slate-900 mb-8 text-center">
            {h.timeline.title}
          </h2>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {h.timeline.events.map((event, index) => (
              <div 
                key={index}
                className="flex flex-col items-center text-center"
                data-testid={`timeline-event-${index}`}
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-outfit font-semibold text-sm mb-2" style={{ backgroundColor: palette.lightAccent }}>
                  {event.year}
                </div>
                <p className="text-xs text-slate-600 max-w-[120px]">{event.event}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Historical Sections */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 space-y-16">
          {sectionKeys.map((key, index) => {
            const section = h.sections[key];
            const isEven = index % 2 === 0;
            
            return (
              <div 
                key={key}
                data-testid={`history-section-${key}`}
                className={`rounded-[2rem] border ${sectionColors[key]} p-8 md:p-12`}
              >
                <div className={`grid md:grid-cols-2 gap-8 items-center ${isEven ? '' : 'md:flex-row-reverse'}`}>
                  {/* Content */}
                  <div className={isEven ? 'md:order-1' : 'md:order-2'}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: palette.lightAccent }}>
                        {sectionIcons[key]}
                      </div>
                      <div>
                        <h3 className="font-outfit text-2xl font-semibold text-slate-900">
                          {section.title}
                        </h3>
                        <span className="text-sm text-slate-500 font-medium">{section.period}</span>
                      </div>
                    </div>
                    
                    <p className="text-slate-700 leading-relaxed mb-6">
                      {section.content}
                    </p>
                    
                    <div className="bg-white/70 backdrop-blur rounded-xl p-4 border border-white">
                      <div className="flex items-start gap-3">
                        <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-slate-700 font-medium">
                          {section.highlight}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Image */}
                  <div className={isEven ? 'md:order-2' : 'md:order-1'}>
                    <img 
                      src={sectionImages[key]}
                      alt={section.title}
                      className="w-full h-64 md:h-80 object-contain bg-white rounded-2xl shadow-lg p-4"
                    />
                    {sectionImageCredits[key]?.show && (
                      <p className="text-xs text-slate-500 mt-2 text-center italic">
                        {sectionImageCredits[key].text}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Indigenous Trade Section */}
      <section className="py-24 bg-gradient-to-b from-amber-50 to-orange-50" data-testid="indigenous-trade-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <span>🤝</span>
              {language === 'fr' ? "Commerce Historique" : "Historical Trade"}
            </div>
            <h2 className="font-outfit text-4xl sm:text-5xl font-semibold text-slate-900 mb-4">
              {language === 'fr' 
                ? "Le Commerce avec les Premières Nations"
                : "Trade with Indigenous Peoples"
              }
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {language === 'fr'
                ? "Le commerce entre Français et Autochtones reposait principalement sur le troc. La piastre espagnole circulait aussi dans ces réseaux d'échange."
                : "Trade between the French and Indigenous peoples was primarily based on barter. The Spanish piastre also circulated in these exchange networks."
              }
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Left - Image Collage */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {/* Main large image - River/Forest */}
                <div className="col-span-2">
                  <img 
                    src="https://images.pexels.com/photos/34627495/pexels-photo-34627495.jpeg?auto=compress&cs=tinysrgb&w=800"
                    alt={language === 'fr' ? "Rivière en automne - Route des fourrures" : "Autumn river - Fur trade route"}
                    className="w-full h-56 object-cover rounded-2xl shadow-lg"
                  />
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    {language === 'fr' ? "Les rivières étaient les autoroutes du commerce des fourrures" : "Rivers were the highways of the fur trade"}
                  </p>
                </div>
                
                {/* Beaver */}
                <div>
                  <img 
                    src="https://images.pexels.com/photos/20489108/pexels-photo-20489108.jpeg?auto=compress&cs=tinysrgb&w=400"
                    alt={language === 'fr' ? "Castor - Or brun de la Nouvelle-France" : "Beaver - Brown gold of New France"}
                    className="w-full h-40 object-cover rounded-2xl shadow-lg"
                  />
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    {language === 'fr' ? "Le castor: \"or brun\"" : "Beaver: \"brown gold\""}
                  </p>
                </div>
                
                {/* Trade goods - Glass beads */}
                <div>
                  <img 
                    src="https://images.unsplash.com/photo-1621364234603-eb56ab321c3c?auto=format&fit=crop&w=400&q=80"
                    alt={language === 'fr' ? "Perles de verre - Marchandises de troc" : "Glass beads - Trade goods"}
                    className="w-full h-40 object-cover rounded-2xl shadow-lg"
                  />
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    {language === 'fr' ? "Perles de verre de troc" : "Trade glass beads"}
                  </p>
                </div>
              </div>
            </div>

            {/* Right - Content */}
            <div>
              <h3 className="font-outfit text-2xl font-semibold text-slate-900 mb-6">
                {language === 'fr' ? "Le Troc: Base du Commerce" : "Barter: The Foundation of Trade"}
              </h3>
              
              <div className="space-y-6 text-slate-700">
                <p className="text-lg leading-relaxed">
                  {language === 'fr'
                    ? "Les coureurs des bois français échangeaient avec les nations Algonquines, Huronnes-Wendats, et Innus. Les fourrures de castor, très prisées en Europe pour la chapellerie, étaient la principale monnaie d'échange."
                    : "French coureurs des bois traded with the Algonquin, Huron-Wendat, and Innu nations. Beaver furs, highly prized in Europe for hat-making, were the main currency of exchange."
                  }
                </p>
                
                <p className="leading-relaxed">
                  {language === 'fr'
                    ? "En retour, les Français offraient des objets en fer (haches, couteaux, chaudrons), des perles de verre vénitiennes, de la porcelaine, et des tissus — des biens impossibles à produire localement."
                    : "In return, the French offered iron objects (axes, knives, kettles), Venetian glass beads, porcelain, and fabrics — goods impossible to produce locally."
                  }
                </p>
              </div>

              {/* Trade Items Grid */}
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm">
                  <div className="text-2xl mb-2">🦫</div>
                  <h4 className="font-semibold text-slate-900 text-sm">
                    {language === 'fr' ? "Fourrures de Castor" : "Beaver Furs"}
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">
                    {language === 'fr' ? "Exportées vers l'Europe" : "Exported to Europe"}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm">
                  <div className="text-2xl mb-2">🪓</div>
                  <h4 className="font-semibold text-slate-900 text-sm">
                    {language === 'fr' ? "Outils en Fer" : "Iron Tools"}
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">
                    {language === 'fr' ? "Haches, couteaux, chaudrons" : "Axes, knives, kettles"}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm">
                  <div className="text-2xl mb-2">📿</div>
                  <h4 className="font-semibold text-slate-900 text-sm">
                    {language === 'fr' ? "Perles de Verre" : "Glass Beads"}
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">
                    {language === 'fr' ? "Vénitiennes et françaises" : "Venetian and French"}
                  </p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm">
                  <div className="text-2xl mb-2">💰</div>
                  <h4 className="font-semibold text-slate-900 text-sm">
                    {language === 'fr' ? "Piastres Espagnoles" : "Spanish Piastres"}
                  </h4>
                  <p className="text-xs text-slate-500 mt-1">
                    {language === 'fr' ? "Monnaie universelle" : "Universal currency"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Piastre in Trade - Special Section */}
          <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-3xl p-8 md:p-12 border border-amber-200">
            <div className="grid md:grid-cols-3 gap-8 items-center">
              {/* Left - Coin image */}
              <div className="text-center">
                <div 
                  className="w-32 h-32 mx-auto rounded-full shadow-2xl"
                  style={{
                    background: 'linear-gradient(145deg, #ffd700, #b8860b, #ffd700)',
                    boxShadow: '0 20px 40px rgba(180, 120, 50, 0.4)'
                  }}
                >
                  <div className="w-full h-full rounded-full flex items-center justify-center">
                    <span className="text-5xl font-serif font-bold text-amber-900">₱</span>
                  </div>
                </div>
                <p className="text-sm text-amber-800 font-medium mt-4">
                  {language === 'fr' ? "Piastre Espagnole" : "Spanish Piastre"}
                </p>
              </div>

              {/* Center & Right - Content */}
              <div className="md:col-span-2">
                <h3 className="font-outfit text-2xl font-semibold text-slate-900 mb-4">
                  {language === 'fr' 
                    ? "La Piastre dans le Commerce Autochtone"
                    : "The Piastre in Indigenous Trade"
                  }
                </h3>
                <p className="text-slate-700 leading-relaxed mb-4">
                  {language === 'fr'
                    ? "Bien que le troc dominait, la piastre espagnole servait de référence de valeur. Les archives montrent que certains postes de traite utilisaient la piastre pour établir les prix: « une peau de castor vaut 1/8 de piastre » ou « un fusil vaut 10 piastres en fourrures »."
                    : "Although barter dominated, the Spanish piastre served as a reference of value. Records show that some trading posts used the piastre to set prices: \"one beaver pelt is worth 1/8 of a piastre\" or \"a rifle is worth 10 piastres in furs\"."
                  }
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-white/70 text-amber-800 rounded-full text-sm font-medium">
                    🏕️ {language === 'fr' ? "Postes de traite" : "Trading posts"}
                  </span>
                  <span className="px-4 py-2 bg-white/70 text-amber-800 rounded-full text-sm font-medium">
                    🛶 {language === 'fr' ? "Coureurs des bois" : "Coureurs des bois"}
                  </span>
                  <span className="px-4 py-2 bg-white/70 text-amber-800 rounded-full text-sm font-medium">
                    📜 {language === 'fr' ? "Compagnie de la Baie d'Hudson" : "Hudson's Bay Company"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Historical Quote */}
          <div className="mt-12 text-center">
            <blockquote className="font-outfit text-xl md:text-2xl text-slate-700 italic max-w-3xl mx-auto">
              {language === 'fr'
                ? "« Le castor fait tout, il nous donne des chaudières, des haches, des épées, des couteaux, du pain; et finalement, il fait tout. »"
                : "\"The beaver does everything, it gives us kettles, axes, swords, knives, bread; and finally, it does everything.\""
              }
            </blockquote>
            <cite className="text-slate-500 text-sm mt-4 block">
              — {language === 'fr' ? "Chef autochtone, vers 1650" : "Indigenous Chief, circa 1650"}
            </cite>
          </div>
        </div>
      </section>

      {/* Interactive Trade Routes Map */}
      <section className="py-24 bg-slate-900" data-testid="trade-routes-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-white mb-4 text-center">
            {language === 'fr' ? "Routes Commerciales de la Piastre" : "Piastre Trade Routes"}
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            {language === 'fr' 
              ? "Comment les pièces de huit espagnoles ont voyagé du Mexique jusqu'au Québec au XVIIe-XVIIIe siècle"
              : "How Spanish pieces of eight traveled from Mexico to Quebec in the 17th-18th century"
            }
          </p>
          
          {/* Interactive Map Container */}
          <div className="relative bg-slate-800 rounded-3xl p-4 md:p-8 overflow-hidden">
            {/* Map Background */}
            <div 
              className="relative w-full h-[500px] md:h-[600px] rounded-2xl overflow-hidden"
              style={{
                background: `linear-gradient(135deg, #1e3a5f 0%, #0f2744 50%, #1a3a4f 100%)`
              }}
            >
              {/* Simplified Atlantic Map SVG */}
              <svg 
                viewBox="0 0 800 500" 
                className="w-full h-full"
                style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }}
              >
                {/* Ocean Background */}
                <rect width="800" height="500" fill="#1e3a5f" />
                
                {/* Grid lines for ocean effect */}
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#2a4a6f" strokeWidth="0.5" opacity="0.3"/>
                  </pattern>
                  
                  {/* Animated dash for trade routes */}
                  <linearGradient id="routeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#fbbf24" />
                    <stop offset="50%" stopColor="#f59e0b" />
                    <stop offset="100%" stopColor="#fbbf24" />
                  </linearGradient>
                </defs>
                
                <rect width="800" height="500" fill="url(#grid)" />
                
                {/* North America - simplified coastline */}
                <path 
                  d="M 50 80 Q 100 90, 120 120 Q 140 150, 150 180 Q 160 220, 180 260 Q 200 300, 220 340 Q 240 380, 280 400 Q 320 420, 380 430 Q 420 435, 450 420"
                  fill="#3d5a4a"
                  stroke="#4a6b5a"
                  strokeWidth="2"
                />
                
                {/* Florida */}
                <path 
                  d="M 280 400 Q 290 420, 300 450 Q 305 470, 295 480 Q 280 475, 275 460 Q 270 440, 280 400"
                  fill="#3d5a4a"
                  stroke="#4a6b5a"
                  strokeWidth="1"
                />
                
                {/* Mexico/Central America */}
                <path 
                  d="M 50 350 Q 80 360, 100 380 Q 120 400, 150 420 Q 180 440, 220 450 Q 250 455, 280 445"
                  fill="#3d5a4a"
                  stroke="#4a6b5a"
                  strokeWidth="2"
                />
                
                {/* Caribbean Islands */}
                <ellipse cx="350" cy="430" rx="15" ry="8" fill="#4a6b5a" /> {/* Cuba */}
                <ellipse cx="400" cy="445" rx="8" ry="5" fill="#4a6b5a" /> {/* Hispaniola */}
                <ellipse cx="430" cy="455" rx="5" ry="3" fill="#4a6b5a" /> {/* Puerto Rico */}
                
                {/* South America - top */}
                <path 
                  d="M 380 480 Q 420 470, 500 490 L 500 500 L 300 500 Q 340 490, 380 480"
                  fill="#3d5a4a"
                  stroke="#4a6b5a"
                  strokeWidth="2"
                />
                
                {/* Europe - West coast */}
                <path 
                  d="M 650 80 Q 680 100, 700 140 Q 720 180, 730 220 Q 740 260, 750 300 Q 755 340, 750 380"
                  fill="#3d5a4a"
                  stroke="#4a6b5a"
                  strokeWidth="2"
                />
                
                {/* Africa - West coast */}
                <path 
                  d="M 680 380 Q 700 400, 720 440 Q 740 480, 760 500 L 800 500 L 800 350 Q 750 360, 680 380"
                  fill="#3d5a4a"
                  stroke="#4a6b5a"
                  strokeWidth="2"
                />
                
                {/* TRADE ROUTES */}
                
                {/* Route 1: Mexico to Caribbean */}
                <path 
                  d="M 120 400 Q 200 420, 280 430 Q 320 435, 350 430"
                  fill="none"
                  stroke="#fbbf24"
                  strokeWidth="3"
                  strokeDasharray="10,5"
                  opacity="0.8"
                  className="animate-pulse"
                />
                
                {/* Route 2: Caribbean to English Colonies (Boston/New York) */}
                <path 
                  d="M 350 430 Q 320 380, 280 320 Q 250 260, 200 200 Q 180 160, 170 130"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="3"
                  strokeDasharray="10,5"
                  opacity="0.8"
                  className="animate-pulse"
                />
                
                {/* Route 3: English Colonies to Quebec */}
                <path 
                  d="M 170 130 Q 140 110, 120 100"
                  fill="none"
                  stroke="#eab308"
                  strokeWidth="3"
                  strokeDasharray="10,5"
                  opacity="0.8"
                  className="animate-pulse"
                />
                
                {/* Route 4: Direct Spain route (secondary) */}
                <path 
                  d="M 350 430 Q 500 380, 650 280 Q 700 240, 720 200"
                  fill="none"
                  stroke="#94a3b8"
                  strokeWidth="2"
                  strokeDasharray="8,4"
                  opacity="0.4"
                />
                
                {/* LOCATION MARKERS */}
                
                {/* Mexico City - Origin */}
                <g className="cursor-pointer" data-location="mexico">
                  <circle cx="100" cy="400" r="12" fill="#dc2626" stroke="#fff" strokeWidth="2">
                    <animate attributeName="r" values="12;14;12" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  <text x="100" y="430" textAnchor="middle" fill="#fff" fontSize="11" fontWeight="bold">
                    MEXICO
                  </text>
                </g>
                
                {/* Caribbean Hub */}
                <g className="cursor-pointer" data-location="caribbean">
                  <circle cx="350" cy="430" r="10" fill="#f59e0b" stroke="#fff" strokeWidth="2">
                    <animate attributeName="r" values="10;12;10" dur="2s" repeatCount="indefinite" begin="0.5s"/>
                  </circle>
                  <text x="350" y="410" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">
                    CARAÏBES
                  </text>
                </g>
                
                {/* Boston/New York - English Colonies */}
                <g className="cursor-pointer" data-location="colonies">
                  <circle cx="200" cy="200" r="10" fill="#3b82f6" stroke="#fff" strokeWidth="2">
                    <animate attributeName="r" values="10;12;10" dur="2s" repeatCount="indefinite" begin="1s"/>
                  </circle>
                  <text x="220" y="195" textAnchor="start" fill="#fff" fontSize="10" fontWeight="bold">
                    {language === 'fr' ? "COLONIES" : "COLONIES"}
                  </text>
                  <text x="220" y="208" textAnchor="start" fill="#94a3b8" fontSize="9">
                    Boston, New York
                  </text>
                </g>
                
                {/* Quebec - Destination */}
                <g className="cursor-pointer" data-location="quebec">
                  <circle cx="120" cy="100" r="14" fill="#10b981" stroke="#fff" strokeWidth="3">
                    <animate attributeName="r" values="14;16;14" dur="2s" repeatCount="indefinite" begin="1.5s"/>
                  </circle>
                  <text x="140" y="95" textAnchor="start" fill="#fff" fontSize="12" fontWeight="bold">
                    QUÉBEC
                  </text>
                  <text x="140" y="110" textAnchor="start" fill="#a7f3d0" fontSize="10">
                    {language === 'fr' ? "Destination" : "Destination"}
                  </text>
                </g>
                
                {/* Spain (secondary route) */}
                <g className="cursor-pointer opacity-60" data-location="spain">
                  <circle cx="720" cy="200" r="8" fill="#94a3b8" stroke="#fff" strokeWidth="1"/>
                  <text x="700" y="180" textAnchor="end" fill="#94a3b8" fontSize="9">
                    ESPAGNE
                  </text>
                </g>
                
                {/* Legend */}
                <g transform="translate(580, 40)">
                  <rect x="0" y="0" width="200" height="120" rx="10" fill="#0f172a" opacity="0.8"/>
                  <text x="15" y="25" fill="#fff" fontSize="11" fontWeight="bold">
                    {language === 'fr' ? "LÉGENDE" : "LEGEND"}
                  </text>
                  
                  <line x1="15" y1="45" x2="45" y2="45" stroke="#fbbf24" strokeWidth="3" strokeDasharray="5,3"/>
                  <text x="55" y="49" fill="#e2e8f0" fontSize="10">
                    {language === 'fr' ? "Route principale" : "Main route"}
                  </text>
                  
                  <line x1="15" y1="65" x2="45" y2="65" stroke="#94a3b8" strokeWidth="2" strokeDasharray="5,3"/>
                  <text x="55" y="69" fill="#94a3b8" fontSize="10">
                    {language === 'fr' ? "Route vers l'Espagne" : "Route to Spain"}
                  </text>
                  
                  <circle cx="25" cy="90" r="6" fill="#dc2626"/>
                  <text x="55" y="94" fill="#e2e8f0" fontSize="10">
                    {language === 'fr' ? "Origine (mines)" : "Origin (mines)"}
                  </text>
                  
                  <circle cx="25" cy="108" r="6" fill="#10b981"/>
                  <text x="55" y="112" fill="#e2e8f0" fontSize="10">
                    {language === 'fr' ? "Destination" : "Destination"}
                  </text>
                </g>
                
                {/* Compass Rose */}
                <g transform="translate(60, 450)">
                  <circle cx="0" cy="0" r="25" fill="#0f172a" opacity="0.6"/>
                  <text x="0" y="-8" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="bold">N</text>
                  <text x="0" y="18" textAnchor="middle" fill="#94a3b8" fontSize="8">S</text>
                  <text x="-12" y="5" textAnchor="middle" fill="#94a3b8" fontSize="8">O</text>
                  <text x="12" y="5" textAnchor="middle" fill="#94a3b8" fontSize="8">E</text>
                  <line x1="0" y1="-18" x2="0" y2="-5" stroke="#fff" strokeWidth="2"/>
                </g>
              </svg>
            </div>
            
            {/* Trade Route Description Cards */}
            <div className="grid md:grid-cols-3 gap-4 mt-8">
              <div className="bg-slate-700/50 rounded-xl p-4 border-l-4 border-red-500">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <h4 className="font-outfit font-semibold text-white text-sm">1. {language === 'fr' ? "Mines Mexicaines" : "Mexican Mines"}</h4>
                </div>
                <p className="text-slate-300 text-xs">
                  {language === 'fr' 
                    ? "L'argent extrait à Potosí et Zacatecas était frappé en pièces de huit réaux à Mexico."
                    : "Silver mined in Potosí and Zacatecas was minted into pieces of eight in Mexico City."
                  }
                </p>
              </div>
              
              <div className="bg-slate-700/50 rounded-xl p-4 border-l-4 border-amber-500">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                  <h4 className="font-outfit font-semibold text-white text-sm">2. {language === 'fr' ? "Commerce Colonial" : "Colonial Trade"}</h4>
                </div>
                <p className="text-slate-300 text-xs">
                  {language === 'fr' 
                    ? "Les piastres transitaient par les Caraïbes et les colonies anglaises (Boston, New York) via le commerce illicite."
                    : "Piastres flowed through the Caribbean and English colonies (Boston, New York) via illicit trade."
                  }
                </p>
              </div>
              
              <div className="bg-slate-700/50 rounded-xl p-4 border-l-4 border-emerald-500">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                  <h4 className="font-outfit font-semibold text-white text-sm">3. {language === 'fr' ? "Québec" : "Quebec"}</h4>
                </div>
                <p className="text-slate-300 text-xs">
                  {language === 'fr' 
                    ? "La Nouvelle-France manquait de monnaie française — les piastres comblaient ce vide et devenaient la monnaie courante."
                    : "New France lacked French currency — piastres filled this void and became the common currency."
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Historical Note */}
          <div className="mt-8 p-6 bg-slate-800/50 rounded-2xl border border-slate-700">
            <p className="text-slate-300 text-sm leading-relaxed">
              <span className="text-amber-400 font-semibold">
                {language === 'fr' ? "💡 Fait historique : " : "💡 Historical fact: "}
              </span>
              {language === 'fr' 
                ? "Bien que la Nouvelle-France fût une colonie française, la majorité de son argent provenait des mines espagnoles du Mexique et du Pérou. Les marchands anglais et hollandais servaient d'intermédiaires, échangeant des fourrures canadiennes contre des piastres espagnoles — créant ainsi un réseau commercial triangulaire qui contournait les restrictions coloniales."
                : "Although New France was a French colony, most of its silver came from Spanish mines in Mexico and Peru. English and Dutch merchants served as intermediaries, trading Canadian furs for Spanish piastres — creating a triangular trade network that circumvented colonial restrictions."
              }
            </p>
          </div>
        </div>
      </section>

      {/* Historical Gallery Section */}
      <section className="py-24 bg-white" data-testid="gallery-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-slate-900 mb-4 text-center">
            {language === 'fr' ? "Galerie Historique" : "Historical Gallery"}
          </h2>
          <p className="text-slate-600 text-center mb-12">
            {language === 'fr' 
              ? "Billets, portraits et documents d'époque — tous libres de droits (domaine public)"
              : "Banknotes, portraits and period documents — all royalty-free (public domain)"
            }
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Louis-Joseph Papineau */}
            <div className="bg-slate-50 rounded-2xl p-6 text-center">
              <img 
                src={WIKIMEDIA_IMAGES.papineau}
                alt="Louis-Joseph Papineau"
                className="w-full h-64 object-contain rounded-xl mb-4"
              />
              <h4 className="font-outfit font-semibold text-slate-900 mb-1">Louis-Joseph Papineau</h4>
              <p className="text-sm text-slate-600">
                {language === 'fr' 
                  ? "Chef des Patriotes, 1837 — Daguerréotype ~1852"
                  : "Patriote Leader, 1837 — Daguerreotype ~1852"
                }
              </p>
              <p className="text-xs text-slate-400 mt-2">Bibliothèque et Archives Canada</p>
            </div>
            
            {/* Billet 5 Piastres */}
            <div className="bg-slate-50 rounded-2xl p-6 text-center">
              <img 
                src={WIKIMEDIA_IMAGES.billetPiastres5}
                alt="Billet de 5 piastres"
                className="w-full h-64 object-contain rounded-xl mb-4"
              />
              <h4 className="font-outfit font-semibold text-slate-900 mb-1">
                {language === 'fr' ? "Billet de 5 Piastres" : "5 Piastres Banknote"}
              </h4>
              <p className="text-sm text-slate-600">Banque du Peuple, 1836</p>
              <p className="text-xs text-slate-400 mt-2">Wikimedia Commons</p>
            </div>
            
            {/* Billet Molson */}
            <div className="bg-slate-50 rounded-2xl p-6 text-center">
              <img 
                src={WIKIMEDIA_IMAGES.billetMolson}
                alt="Billet de 2 piastres Molson"
                className="w-full h-64 object-contain rounded-xl mb-4"
              />
              <h4 className="font-outfit font-semibold text-slate-900 mb-1">
                {language === 'fr' ? "Billet de 2 Piastres" : "2 Piastres Banknote"}
              </h4>
              <p className="text-sm text-slate-600">Banque Molson, 1853</p>
              <p className="text-xs text-slate-400 mt-2">Wikimedia Commons</p>
            </div>
            
            {/* Pièce de huit */}
            <div className="bg-slate-50 rounded-2xl p-6 text-center">
              <img 
                src={HISTORY_IMAGES.silverCoins}
                alt="Pièce de huit espagnole"
                className="w-full h-64 object-contain rounded-xl mb-4"
              />
              <h4 className="font-outfit font-semibold text-slate-900 mb-1">
                {language === 'fr' ? "Pièce de Huit" : "Piece of Eight"}
              </h4>
              <p className="text-sm text-slate-600">
                {language === 'fr' ? "8 Réaux espagnols — monnaie qui circulait au Québec" : "Spanish 8 Reales — currency that circulated in Quebec"}
              </p>
              <p className="text-xs text-slate-400 mt-2">Unsplash</p>
            </div>
            
            {/* Monnaie de carte */}
            <div className="bg-slate-50 rounded-2xl p-6 text-center">
              <img 
                src={HISTORY_IMAGES.antiqueMap}
                alt="Monnaie de carte"
                className="w-full h-64 object-cover rounded-xl mb-4"
              />
              <h4 className="font-outfit font-semibold text-slate-900 mb-1">
                {language === 'fr' ? "Monnaie de Carte" : "Card Money"}
              </h4>
              <p className="text-sm text-slate-600">
                {language === 'fr' ? "Cartes à jouer = monnaie officielle (1685-1760)" : "Playing cards = official currency (1685-1760)"}
              </p>
              <p className="text-xs text-slate-400 mt-2">
                <a href="https://www.bankofcanadamuseum.ca" target="_blank" rel="noopener noreferrer" className="hover:text-slate-600">
                  {language === 'fr' ? "Voir au Musée de la Banque du Canada" : "View at Bank of Canada Museum"}
                </a>
              </p>
            </div>
            
            {/* Billet Banque Provinciale */}
            <div className="bg-slate-50 rounded-2xl p-6 text-center">
              <img 
                src={WIKIMEDIA_IMAGES.billetProvinciale}
                alt="Billet Banque Provinciale"
                className="w-full h-64 object-contain rounded-xl mb-4"
              />
              <h4 className="font-outfit font-semibold text-slate-900 mb-1">
                {language === 'fr' ? "Cinq Piastres" : "Five Piastres"}
              </h4>
              <p className="text-sm text-slate-600">Banque Provinciale du Canada, 1919</p>
              <p className="text-xs text-slate-400 mt-2">Wikimedia Commons</p>
            </div>
          </div>
          
          {/* Special Feature: Banque de la République du Québec - 1 Piastre */}
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-3xl overflow-hidden">
            <div className="p-6 md:p-8 text-center border-b border-blue-200 bg-blue-100/50">
              <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-3">
                <span>⚜️</span>
                {language === 'fr' ? "PIÈCE DE COLLECTION RARE" : "RARE COLLECTIBLE PIECE"}
              </div>
              <h3 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900">
                {language === 'fr' ? "1 Piastre — Banque de la République du Québec" : "1 Piastre — Bank of the Republic of Quebec"}
              </h3>
              <p className="text-slate-600 mt-2">
                {language === 'fr' 
                  ? "Billet de fantaisie indépendantiste — \"Négociable entre membres indépendantistes seulement\""
                  : "Independence fantasy note — \"Negotiable between independence supporters only\""
                }
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-0">
              {/* Recto (Avers) */}
              <div className="p-6 md:p-8 bg-white/50">
                <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-3">
                  {language === 'fr' ? "RECTO (AVERS)" : "FRONT (OBVERSE)"}
                </p>
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/24bd8076-e193-410c-921d-6cca767cc754/images/2e7fb0f3bd803bc91be8a42e3f125434bfbb29db3a377276773e1d6104a84cf0.png"
                  alt="1 Piastre - Banque de la République du Québec - Avers"
                  className="w-full rounded-xl shadow-lg border border-slate-200"
                />
                <div className="mt-4 text-sm text-slate-600 space-y-1">
                  <p><strong>{language === 'fr' ? "Portrait:" : "Portrait:"}</strong> Chénier</p>
                  <p><strong>{language === 'fr' ? "Inscription:" : "Inscription:"}</strong> "QUEBEC LIBRE"</p>
                </div>
              </div>
              
              {/* Verso (Revers) */}
              <div className="p-6 md:p-8 bg-blue-50/50 border-t md:border-t-0 md:border-l border-blue-200">
                <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-3">
                  {language === 'fr' ? "VERSO (REVERS)" : "BACK (REVERSE)"}
                </p>
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/24bd8076-e193-410c-921d-6cca767cc754/images/dfe5687210d3e308dc7787418eae0c42d02ace4a296002221fbe1eea1d6d1458.png"
                  alt="1 Piastre - Banque de la République du Québec - Revers"
                  className="w-full rounded-xl shadow-lg border border-slate-200"
                />
                <div className="mt-4 text-sm text-slate-600">
                  <p><strong>{language === 'fr' ? "Inscription:" : "Inscription:"}</strong> "BANQUE DE LA RÉPUBLIQUE DU QUÉBEC - UNE PIASTRE"</p>
                </div>
              </div>
            </div>
            
            {/* Details & Source */}
            <div className="p-6 md:p-8 bg-slate-100/80 border-t border-blue-200">
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div>
                  <p className="text-slate-700 text-sm leading-relaxed">
                    {language === 'fr'
                      ? "Ce billet de fantaisie porte l'inscription: « CE BON PIASTRE EST GARANTI PAR LES RICHESSES NATURELLES DE LA FUTURE RÉPUBLIQUE ». Il représente un symbole du mouvement indépendantiste québécois et témoigne de l'attachement culturel au terme « piastre »."
                      : "This fantasy note bears the inscription: \"THIS PIASTRE VOUCHER IS GUARANTEED BY THE NATURAL RESOURCES OF THE FUTURE REPUBLIC.\" It represents a symbol of the Quebec independence movement and demonstrates the cultural attachment to the term 'piastre'."
                    }
                  </p>
                </div>
                <div className="text-sm text-slate-500 space-y-2">
                  <p><strong>{language === 'fr' ? "Dimensions:" : "Dimensions:"}</strong> 151 × 69 mm</p>
                  <p><strong>{language === 'fr' ? "Type:" : "Type:"}</strong> {language === 'fr' ? "Billet de fantaisie" : "Fantasy banknote"}</p>
                  <p><strong>Source:</strong> 
                    <a 
                      href="https://fr.numista.com/catalogue/note221864.html" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 underline ml-1"
                    >
                      Numista.com (N# 221864)
                    </a>
                  </p>
                  <p className="text-xs text-slate-400">© bbybugs (CC BY-NC-SA)</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Reward Poster - Papineau 4000 Piastres - AUTHENTIC IMAGE */}
          <div className="mt-12 bg-amber-50 border-2 border-amber-300 rounded-3xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image */}
              <div className="bg-amber-100 p-6 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_crypto-app-builder/artifacts/i31rzgaz_louis-joseph-papineau-mis-a-prix-4000-piastres.jpg"
                  alt="Affiche de récompense - Louis-Joseph Papineau - 4000 Piastres"
                  className="w-full max-w-md rounded-xl shadow-2xl border-4 border-amber-200"
                  style={{ 
                    boxShadow: '0 25px 50px rgba(180, 120, 50, 0.4)',
                    transform: 'rotate(-1deg)'
                  }}
                />
              </div>
              
              {/* Content */}
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-bold mb-4 w-fit">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  {language === 'fr' ? "DOCUMENT HISTORIQUE AUTHENTIQUE" : "AUTHENTIC HISTORICAL DOCUMENT"}
                </div>
                
                <h3 className="font-outfit text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                  {language === 'fr' ? "4,000 Piastres de Récompense!" : "4,000 Piastres Reward!"}
                </h3>
                
                <p className="text-slate-700 text-lg leading-relaxed mb-6">
                  {language === 'fr' 
                    ? "En 1837, lors de la Rébellion des Patriotes, le gouverneur Gosford émet cette proclamation bilingue offrant une récompense de 4,000 piastres (équivalent à 1,000 livres sterling) pour la capture de Louis-Joseph Papineau, accusé de haute trahison."
                    : "In 1837, during the Patriotes Rebellion, Governor Gosford issued this bilingual proclamation offering a reward of 4,000 piastres (equivalent to 1,000 pounds sterling) for the capture of Louis-Joseph Papineau, charged with high treason."
                  }
                </p>
                
                <div className="bg-white/70 rounded-xl p-4 mb-6">
                  <p className="text-sm text-slate-600 italic">
                    {language === 'fr' 
                      ? "« ATTENDU que, par information sous serment, il appert que, LOUIS JOSEPH PAPINEAU, de la cité de Montréal, Ecuyer, est accusé du crime de Haute Trahison... »"
                      : "\"WHEREAS by information upon oath it appears that, LOUIS JOSEPH PAPINEAU, of the city of Montreal, Esquire, is charged with the crime of High Treason...\""
                    }
                  </p>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-amber-200 text-amber-800 rounded-full text-sm font-medium">
                    📜 1837
                  </span>
                  <span className="px-4 py-2 bg-amber-200 text-amber-800 rounded-full text-sm font-medium">
                    🏛️ {language === 'fr' ? "Province du Bas-Canada" : "Province of Lower Canada"}
                  </span>
                  <span className="px-4 py-2 bg-amber-200 text-amber-800 rounded-full text-sm font-medium">
                    💰 4,000 ₱ = £1,000
                  </span>
                </div>
                
                <p className="text-xs text-slate-500 mt-6">
                  {language === 'fr' 
                    ? "Ce document prouve l'utilisation officielle du terme « piastre » dans les documents gouvernementaux de l'époque."
                    : "This document proves the official use of the term 'piastre' in government documents of the era."
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fun Facts */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-white mb-12 text-center">
            {h.funFacts.title}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {h.funFacts.facts.map((fact, index) => (
              <div 
                key={index}
                data-testid={`fun-fact-${index}`}
                className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-400 font-outfit font-bold">{index + 1}</span>
                  </div>
                  <p className="text-slate-300 leading-relaxed">{fact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24" style={{ backgroundColor: `${palette.primary}15` }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <Quote className="w-12 h-12 mx-auto mb-6" style={{ color: palette.sectionAccent }} />
          <blockquote className="font-outfit text-2xl sm:text-3xl font-medium text-slate-900 leading-relaxed mb-6">
            "{h.quote.text}"
          </blockquote>
          <cite className="text-slate-600 font-medium">— {h.quote.author}</cite>
        </div>
      </section>

      {/* Sources & References */}
      <section className="py-24 bg-white" data-testid="sources-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-slate-900 mb-4 text-center">
            {h.sources.title}
          </h2>
          <p className="text-slate-600 text-center mb-12">
            {language === 'fr' 
              ? "Ces sources ont été consultées pour la rédaction de cette page."
              : "These sources were consulted for the writing of this page."
            }
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {h.sources.list.map((source, index) => (
              <a
                key={index}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`source-link-${index}`}
                className="group bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:border-slate-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-outfit font-semibold text-slate-900 transition-colors mb-2" style={{ '--hover-color': palette.lightAccent }}>
                      {source.name}
                    </h4>
                    <p className="text-sm text-slate-600">{source.desc}</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-slate-400 flex-shrink-0 transition-colors" style={{ color: palette.sectionAccent }} />
                </div>
              </a>
            ))}
          </div>
          
          {/* Additional Image Credits */}
          <div className="mt-12 p-6 bg-slate-100 rounded-2xl">
            <h4 className="font-outfit font-semibold text-slate-900 mb-4">
              {language === 'fr' ? "Crédits Images" : "Image Credits"}
            </h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li>• Unsplash.com — {language === 'fr' ? "Images libres de droits" : "Royalty-free images"}</li>
              <li>• Pexels.com — {language === 'fr' ? "Photos gratuites" : "Free stock photos"}</li>
              <li>• Wikimedia Commons — {language === 'fr' ? "Domaine public : pièce de huit, monnaie de carte, carte Nouvelle-France" : "Public domain: piece of eight, card money, New France map"}</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24" style={{ background: `linear-gradient(135deg, ${palette.primaryDark}, ${palette.text || '#1e293b'})` }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl font-semibold text-white mb-6">
            {h.cta.title}
          </h2>
          <p className="text-xl text-white/60 mb-10">
            {h.cta.description}
          </p>
          <Link 
            to="/"
            data-testid="cta-discover-btn"
            className="inline-flex items-center gap-3 px-10 py-5 rounded-full font-semibold text-lg transition-all"
            style={{ 
              background: `linear-gradient(135deg, ${palette.primary}, ${palette.accent})`,
              color: palette.text
            }}
          >
            {h.cta.button}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HistoryPage;
